import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingFacade } from '../../../../../Facades/onboarding.facade';
import { AuthFacade } from '../../../../../Facades/auth.facade';
import { Onboarding } from '../../../../../Models/onboarding.model';

@Component({
  selector: 'app-company-setup',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './company-setup.component.html',
  styleUrl: './company-setup.component.scss'
})
export class CompanySetupComponent implements OnInit {
  private onboardingService = inject(OnboardingFacade);
  private authFacade = inject(AuthFacade);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);

  mainForm!: FormGroup;
  loading = false;
  errorMessage = '';
  private nicknameEditedByUser = false;

  private readonly resolvedFirstName = computed(() =>
    this.resolveName(
      this.onboardingService.onboarding()?.onboarder_data?.first_name,
      this.authFacade.currentUser()?.first_name
    )
  );

  private readonly resolvedLastName = computed(() =>
    this.resolveName(
      this.onboardingService.onboarding()?.onboarder_data?.last_name,
      this.authFacade.currentUser()?.last_name
    )
  );

  constructor() {
    this.buildForm();
    this.watchOrgNameForNickname();
    this.watchNicknameEdits();
    this.syncIdentityToForm();
  }

  ngOnInit(): void {
    this.loadOnboarding();
  }

  private buildForm(): void {
    this.mainForm = this.fb.group({
      company_data: this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2)]],
        domain: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/)]],
        avyna_nickname: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9-]+$/)]]
      }),
      onboarder_data: this.fb.group({
        first_name: [{ value: '', disabled: true }],
        last_name: [{ value: '', disabled: true }],
        phone: ['']
      })
    });
  }

  private syncIdentityToForm(): void {
    effect(() => {
      const onboarding = this.onboardingService.onboarding();
      const firstName = this.resolvedFirstName();
      const lastName = this.resolvedLastName();
      const user = this.authFacade.currentUser();
      this.prefillCompany(onboarding);
      this.prefillName('first_name', firstName);
      this.prefillName('last_name', lastName);
      this.prefillPhone(onboarding?.onboarder_data?.phone, user?.mobilePhone);
    });
  }

  private watchOrgNameForNickname(): void {
    const nameControl = this.mainForm.get('company_data.name') as AbstractControl;
    nameControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((name: string) => this.applyDerivedNickname(name));
  }

  private watchNicknameEdits(): void {
    const nicknameControl = this.mainForm.get('company_data.avyna_nickname') as AbstractControl;
    nicknameControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.markNicknameEdited());
  }

  private applyDerivedNickname(name: string): void {
    if (this.nicknameEditedByUser) return;
    const control = this.mainForm.get('company_data.avyna_nickname') as AbstractControl;
    control.setValue(this.deriveNickname(name), { emitEvent: false });
  }

  private markNicknameEdited(): void {
    this.nicknameEditedByUser = true;
  }

  private deriveNickname(name: string): string {
    return (name ?? '').replace(/[^a-zA-Z]/g, '').toUpperCase();
  }

  private prefillCompany(onboarding: Onboarding | null): void {
    const company = onboarding?.company_data;
    if (!company) return;
    const control = this.mainForm.get('company_data') as AbstractControl;
    if (!control.pristine) return;
    const savedNickname = (company.avyna_nickname || '').trim();
    this.nicknameEditedByUser = !!savedNickname;
    control.patchValue({
      name: company.name || '',
      domain: company.domain || '',
      avyna_nickname: savedNickname || this.deriveNickname(company.name || '')
    }, { emitEvent: false });
  }

  private prefillName(controlName: string, value: string): void {
    const control = this.mainForm.get(`onboarder_data.${controlName}`) as AbstractControl;
    if (!control.pristine) return;
    control.setValue(value, { emitEvent: false });
    control.disable({ emitEvent: false });
  }

  private prefillPhone(onboarderPhone?: string | null, userPhone?: string | null): void {
    const control = this.mainForm.get('onboarder_data.phone') as AbstractControl;
    if (!control.pristine) return;
    control.setValue(onboarderPhone || userPhone || '', { emitEvent: false });
  }

  private resolveName(primary?: string | null, fallback?: string | null): string {
    return this.normalizeName(primary) || this.normalizeName(fallback);
  }

  private normalizeName(raw?: string | null): string {
    const trimmed = (raw ?? '').trim();
    if (!trimmed || trimmed.toUpperCase() === 'N/A') return '';
    return trimmed;
  }

  loadOnboarding(): void {
    if (this.onboardingService.onboarding()) return;
    this.onboardingService.get().subscribe({
      error: () => {}
    });
  }

  onSubmit(): void {
    if (this.mainForm.invalid) {
      this.mainForm.markAllAsTouched();
      this.errorMessage = this.translate.instant('onboarding.setup.companySetup.errors.requiredFields');
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.onboardingService.saveCompanyAndOnboarderData(this.mainForm.getRawValue()).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigate([`/setup/plan-selection`]);
      },
      error: (error) => {
        this.loading = false;
        this.errorMessage =
          error.error?.message ||
          this.translate.instant('onboarding.setup.companySetup.errors.saveFailed');
      }
    });
  }
}
